import { toast } from '@mx/vix';
import { normalizeError, delay } from '@mx/pay-common';
import {
  SignErrorType,
  bridge,
  env,
  guideSign,
  monitor,
  SignState,
  RETRY_COUNT,
  getSignStatus,
  QueryStatusType,
  CreditType,
  Runtime,
  NativeBridgeErrorCode,
  OpenType,
  directpay,
  postUniteSign,
  queryOrder,
  OpenStatus,
  QueryOrderStatus,
  PayType,
  metricPoints,
  SignStatusType,
  ToastType,
} from '@/common';
import {
  delayPayCashierPaySuccess,
  delayPaydowngradeStandardCashier,
  deferIndependenceSignFail,
  deferIndependenceSignSuccess,
} from './callbackNativeRouter';
import { storeInstance } from '@/store';
import { showPayDelaySignResultPage } from './showResultPage';

const store = storeInstance();
// 支付结果页数据
let promotion = {};
class WXScorePay {
  private outRequestNo = '';

  private isRepeatQuerying = false;

  private isBridgeSucceed = false;

  private bridgeSucceedOnceCheck = false;

  signedFlag = false;

  open(params: any): Promise<any> {
    this.isBridgeSucceed = false;
    this.outRequestNo = params.outRequestNo;
    if ([Runtime.Test, Runtime.Local, Runtime.Dev].includes(env.runtime)) {
      bridge.openPage(params.jumpLink);
      this.isBridgeSucceed = true;
      return Promise.resolve();
    } else {
      let jumpLink = params.jumpLink;
      if (env.creditType === CreditType.WeiXin || env.payType === PayType.WeiXin) {
        jumpLink = `apply_permissions_token=${jumpLink}`;
      }
      return (
        bridge
          .notifyNative('pay.openWeixinScorePay', {
            businessType: 'wxpayScoreEnable',
            query: jumpLink,
            extInfo: '{ "miniProgramType": 0 }',
          })
          .then(() => {
            this.isBridgeSucceed = true;
            return this.checkWXStatus('bridge-success');
          })
          // eslint-disable-next-line consistent-return
          .catch((error) => {
            this.isBridgeSucceed = false;
            monitor.captureInfo({
              name: `openWeixinScorePay 签约异常 errorCode:${error.errorCode}, errorMsg: ${error.errorMsg}`,
              msg: {
                error: normalizeError(error),
                data: {
                  outRequestNo: params.outRequestNo,
                  jumpLink,
                },
              },
            });
            if (error.errorCode === NativeBridgeErrorCode.Fail && error.errorMsg) {
              // 失败
              wxScorePaySignFail(error.errorMsg, SignErrorType.BridgeError);
            } else if (error.errorCode === NativeBridgeErrorCode.Cancel) {
              // 取消：用户真正的取消 + 签约成功的取消 「不点击完成，手动返回」
              return this.checkWXStatus('bridge-cancel');
            }
          })
      );
    }
  }

  subscribeCheckStatus(): Promise<any> {
    return bridge
      .subscribe({
        action: 'foreground',
        handle: () => {
          this.checkWXStatus('foreground');
          bridge
            .unsubscribe({
              action: 'foreground',
            })
            .catch((error) => {
              monitor.captureJSErrorMessage('取消 unsubscribe 事件失败', { error: normalizeError(error) });
            });
        },
      })
      .catch((error) => {
        monitor.captureJSErrorMessage('foreground 事件注册失败', { error: normalizeError(error) });
        wxScorePaySignFail('开通失败，请稍后再试', SignErrorType.ApiError);
      });
  }

  checkWXStatus(flag: string): Promise<any> {
    return this.startRepeatQuery()
      .then((data) => {
        // 客户端回调成功，但前端未能查询到结果
        if (data === 'loopFinished') {
          if (this.isBridgeSucceed && this.bridgeSucceedOnceCheck) {
            this.bridgeSucceedOnceCheck = true;
            monitor.captureMessage(`客户端回调成功，前端未能查询到结果,查询异常，来源: ${flag}`);
            this.checkWXStatus('refresh-modal');
          } else {
            wxScorePaySignFail('开通结果未查询到，请稍后再试', SignErrorType.ApiError);
          }
        }
      })
      .then(() => {
        goDirectPay();
      })
      .catch((error: any) => {
        monitor.captureAjaxErrorMessage(`查询异常，来源: ${flag}`, {
          error: normalizeError(error),
        });
      });
  }

  onceQuery(): Promise<any> {
    return getSignStatus({
      creditType: CreditType.WeiXin,
      outRequestNo: this.outRequestNo,
      ...env.coreBusinessParams,
    })
      .then((data) => {
        return parseInt(data.queryStatus, 10);
      })
      .catch((error) => {
        monitor.captureAjaxErrorMessage(`单次查询签约状态异常: ${error.message || error}`, {
          error: normalizeError(error),
        });
        return QueryStatusType.Fail;
      });
  }

  async startRepeatQuery() {
    if (this.isRepeatQuerying) {
      return;
    }
    toast.show('loading', '开启结果查询中', {
      delay: 0,
    });
    this.isRepeatQuerying = true;
    for (let i = 0; i < RETRY_COUNT; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const status: number = await this.onceQuery();
        if (status === QueryStatusType.Success) {
          this.signedFlag = true;
          monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Success });
          return;
        } else {
          // eslint-disable-next-line no-await-in-loop
          await this.sleep();
        }
      } catch (error: any) {
        this.isRepeatQuerying = false;
        monitor.captureAjaxErrorMessage(`第${i}次查询状态异常:${error?.message || error}`, {
          error: normalizeError(error),
        });
      }
    }
    // 轮询 10 次后没查到结果，重置 isRepeatQuerying 为false
    this.isRepeatQuerying = false;
    // eslint-disable-next-line consistent-return
    return Promise.resolve('loopFinished');
  }

  sleep() {
    return new Promise((resolve) => {
      setTimeout(resolve, 500);
    });
  }
}
const wxScorePay = new WXScorePay();
export async function goDirectPay() {
  // 1、支付流程 && 签约成功
  // 2、独立签约流程 && 签约成功
  if (env.openStatus === OpenStatus.Unopen && !wxScorePay.signedFlag) {
    return;
  }
  toast.hide();
  if (env.openType === OpenType.InpaySign) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { cash_ticket_code, campaign_id, bonus_reduce_yuan, content } =
      store.state.paydelaycashier.displayConsultData?.extendInfo || {};
    const params = {
      ...env.inpayBasicBusinessParams,
      pay_type: env.payType,
      cashier_type: 'preorder-cashier',
      cashticket_code: cash_ticket_code,
      campaign_id,
      bonus_reduce: bonus_reduce_yuan,
      content,
    };
    if (env.openStatus === OpenStatus.Unopen) {
      params.outer_business_data = JSON.stringify({
        openWxCreditAuth: 1,
      });
    }
    store.commit({
      type: 'paydelaycashier/setToast',
      show: true,
      toastType: ToastType.PayLogo,
      toastText: '开通成功',
      toastSubText: '0元下单中...',
      payType: PayType.WeiXin,
    });
    try {
      const payResult = await directpay(params);
      // eslint-disable-next-line promise/catch-or-return
      delay(2000).then(() => {
        promotion = payResult.promotion ?? {};
        // 1秒1次轮询，总共轮询8次
        pollingQueryOrder(8, 1000);
      });
    } catch (error: any) {
      store.commit({ type: 'paydelaycashier/setToast', show: false });
      monitor.captureAjaxErrorMessage('[api]/cashier/directpay接口请求失败', { error: normalizeError(error) });
      wxScorePaySignFail(error?.message || '0元下单异常，可使用其他支付方式完成支付', SignErrorType.ApiError);
    }
  } else {
    const failCallBack = () => {
      toast.show('text', '微信支付分先用后付开通成功');
      // eslint-disable-next-line promise/catch-or-return
      delay(2000).then(() => {
        deferIndependenceSignSuccess();
      });
    };
    // 展示结果页
    showPayDelaySignResultPage(failCallBack);
  }
}
/**
 * 开通微信支付分流程
 * @param jumpLink 微信侧 query 参数
 * @param outRequestNo 微信查询流水号
 */
function openWXScorePay(jumpLink: string, outRequestNo: string): Promise<any> {
  if (jumpLink && outRequestNo) {
    return wxScorePay
      .subscribeCheckStatus()
      .then(() => {
        wxScorePay.open({
          jumpLink,
          outRequestNo,
        });
      })
      .then(() => goDirectPay());
  } else {
    return Promise.reject(new Error('openWXScorePay 方法, jumpLink 或 outRequestNo 为空'));
  }
}
function openWXAuthModal(): Promise<any> {
  const { state = '', bizSerialId = '' } = env.guideSignResponseData;
  const uniteSignParams = {
    ...env.guideSignParams,
    state,
    bizSerialId,
    utmSource: env.utmSource,
  };
  toast.show('loading', '', {
    delay: 0,
  });
  return postUniteSign(uniteSignParams)
    .then(() => {
      toast.hide();
      wxScorePay.signedFlag = true;
      monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Success });
      store.commit({ type: 'result/setSignedStatus', signed: true });
      goDirectPay();
    })
    .catch((error) => {
      monitor.captureAjaxErrorMessage('[api]/mtScorepay/uniteSign接口请求失败', { error: normalizeError(error) });
      wxScorePaySignFail('开通失败，请稍后再试', SignErrorType.ApiError);
    });
}
export function wxScorePaySignFail(text?: string, signErrorType?: SignErrorType) {
  monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Fail });
  if (env.openType === OpenType.InpaySign) {
    if (signErrorType === SignErrorType.ApiError) {
      toast.show('text', text);
    } else if (signErrorType === SignErrorType.BridgeError) {
      toast.show('text', `${text}，可使用其他支付方式完成支付`);
    } else {
      toast.show('text', '0元下单异常，可使用其他支付方式完成支付');
    }
    // eslint-disable-next-line promise/catch-or-return
    delay(2000).then(() => {
      delayPaydowngradeStandardCashier();
    });
  } else if (env.openType === OpenType.IndependenceSign) {
    toast.show('text', text);
    // eslint-disable-next-line promise/catch-or-return
    delay(2000).then(() => {
      deferIndependenceSignFail();
    });
  }
}
function pollingQueryOrder(count: number, interval: number) {
  // 轮询次数为0接口均失败，且 promotion 没有值，则降级标准收银台
  if (count <= 0) {
    store.commit({ type: 'paydelaycashier/setToast', show: false });
    wxScorePaySignFail();
    return;
  }
  setTimeout(() => {
    queryOrder({
      ...env.inpayBasicBusinessParams,
      isauto: 1, // 自动
      query_type: 1, // 仅查询订单状态
      pay_type: PayType.WeiXin,
    })
      .then((res) => {
        env.setQueryOrderTradeStatus(res.trade_status);
        monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: res.trade_status });
        switch (res.trade_status) {
          case QueryOrderStatus.creditsuccess:
            store.commit({ type: 'paydelaycashier/setToast', show: false });
            setTimeout(() => {
              delayPayCashierPaySuccess(promotion);
            }, 100);
            break;
          default:
            pollingQueryOrder(count - 1, interval);
            break;
        }
      })
      .catch((error) => {
        monitor.captureAjaxErrorMessage('[api]queryorder接口请求失败', { error: normalizeError(error) });
        if (count > 0) {
          pollingQueryOrder(count - 1, interval);
        }
      });
  }, interval);
}
export function wxScorePaySign() {
  toast.show('loading', '', {
    delay: 0,
  });
  // 签约请求参数
  const guideSignParams: GuideSignParamsType = {
    ...env.coreBusinessParams,
    creditType: CreditType.WeiXin,
    openType: env.openType,
    // 微信分独立签约所需参数
    lastPromos: store.state.paydelaycashier?.promoInfo?.lastPromos || '',
  };
  monitor.reportMetric(metricPoints.signedStatus, { signStatus: SignStatusType.Start });
  env.setGuideSignParams(guideSignParams);
  guideSign(guideSignParams)
    .then((res) => {
      const { jumpLink = '', outRequestNo = '' } = res;
      env.setGuideSignResponseData(res);
      // 目前只支持独立验证流程，其他流程抛错上报
      if (res.state === SignState.callWXScore) {
        return openWXScorePay(jumpLink, outRequestNo);
      } else if (res.state === SignState.popAuthModal) {
        return openWXAuthModal();
      } else {
        throw new Error('state 暂不支持');
      }
    })
    .catch((error) => {
      monitor.captureAjaxErrorMessage('[api]guideSign引导接口请求失败', { error: normalizeError(error) });
      toast.hide();
      wxScorePaySignFail('开通失败，请稍后再试', SignErrorType.ApiError);
    });
}
